import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useLayoutEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.js';

const PAGE_SIZE = 10;

export function useGenerationCatalogPage(
  identity: string | null,
  indices: number[],
  expandFirst = true,
) {
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const headingRef = useRef<HTMLHeadingElement>(null);
  const pageCount = Math.max(1, Math.ceil(indices.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const visibleIndices = indices.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );
  useLayoutEffect(() => {
    setPage(1);
    setExpanded({});
  }, [identity]);
  useLayoutEffect(() => {
    setPage((previous) => Math.min(previous, pageCount));
  }, [pageCount]);
  function navigate(next: number) {
    setPage(Math.max(1, Math.min(next, pageCount)));
    headingRef.current?.focus({ preventScroll: true });
    headingRef.current?.scrollIntoView?.({
      block: 'start',
      behavior: 'smooth',
    });
  }
  return {
    page: currentPage,
    pageCount,
    visibleIndices,
    headingRef,
    navigate,
    reveal: (index: number) => {
      const position = indices.indexOf(index);
      if (position < 0) return;
      navigate(Math.floor(position / PAGE_SIZE) + 1);
      setExpanded((previous) => ({ ...previous, [index]: true }));
    },
    isExpanded: (index: number) =>
      expanded[index] ??
      (expandFirst && currentPage === 1 && index === indices[0]),
    setExpanded: (index: number, open: boolean) =>
      setExpanded((previous) => ({ ...previous, [index]: open })),
  };
}

export function GenerationCatalogPagination({
  page,
  pageCount,
  count,
  onPage,
  label,
}: {
  page: number;
  pageCount: number;
  count: number;
  onPage: (page: number) => void;
  label: string;
}) {
  if (count <= PAGE_SIZE) return null;
  return (
    <nav
      aria-label={label}
      className="flex shrink-0 flex-nowrap items-center gap-1 whitespace-nowrap text-xs"
    >
      <span
        aria-live={label.endsWith('top') ? 'polite' : undefined}
        aria-atomic="true"
      >
        <span className="sr-only">
          Page {page} of {pageCount}, cases{' '}
        </span>
        {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, count)} of{' '}
        {count}
      </span>
      <Button
        variant="ghost"
        size="icon"
        className="size-8"
        aria-label="Previous"
        disabled={page === 1}
        onClick={() => onPage(page - 1)}
      >
        <ChevronLeftIcon className="size-4" aria-hidden />
      </Button>
      <Select
        value={String(page)}
        onValueChange={(value) => onPage(Number(value))}
      >
        <SelectTrigger
          size="sm"
          aria-label={`${label} page`}
          className="min-w-28 gap-1.5 border-transparent bg-muted/60 px-2.5 text-xs shadow-none hover:bg-muted"
        >
          <SelectValue />
          <span className="text-muted-foreground">of {pageCount}</span>
        </SelectTrigger>
        <SelectContent className="min-w-28">
          {Array.from({ length: pageCount }, (_, index) => (
            <SelectItem key={index + 1} value={String(index + 1)}>
              Page {index + 1}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        variant="ghost"
        size="icon"
        className="size-8"
        aria-label="Next"
        disabled={page === pageCount}
        onClick={() => onPage(page + 1)}
      >
        <ChevronRightIcon className="size-4" aria-hidden />
      </Button>
    </nav>
  );
}
